import {
  type UseInfiniteQueryOptions,
  type UseMutationOptions,
  type UseQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery
} from '@tanstack/react-query'
import type { PanoramaHttpApi } from 'api-validations/http'
import type { ApiClient } from 'utils/ApiClient'
import type { z } from 'zod'

import { useApiClient, useQueryClient } from '.'

type IsVoid<T> = [T] extends [undefined] | [void] ? true : false

type QueryOptions<T extends PanoramaHttpApi.AnyApiDefinition, SelectResult> = Omit<
  UseQueryOptions<PanoramaHttpApi.ParsedResponse<T>, PanoramaHttpApi.MappedError<T>, SelectResult>,
  'queryFn' | 'queryKey'
>

type QueryArgs<T extends PanoramaHttpApi.AnyApiDefinition, SelectResult> =
  IsVoid<PanoramaHttpApi.PathParams<T>> extends true
    ? IsVoid<PanoramaHttpApi.RawRequestBody<T>> extends true
      ? [params?: undefined, options?: QueryOptions<T, SelectResult>]
      : [
          params: undefined,
          options: QueryOptions<T, SelectResult> | undefined,
          body: PanoramaHttpApi.RawRequestBody<T>
        ]
    : IsVoid<PanoramaHttpApi.RawRequestBody<T>> extends true
      ? [params: PanoramaHttpApi.PathParams<T>, options?: QueryOptions<T, SelectResult>]
      : [
          params: PanoramaHttpApi.PathParams<T>,
          options: QueryOptions<T, SelectResult> | undefined,
          body: PanoramaHttpApi.RawRequestBody<T>
        ]

/**
 * A wrapper around `useQuery` that automatically infers the types of the
 * query function and the error type from the API definition.
 *
 * You can supply other options to the query, such as `enabled`, `refetchOnWindowFocus`, etc.
 * but you cannot override the `queryKey` or `queryFn` options, as they are
 * automatically generated from the API definition.
 */
export const usePanoramaQuery = <
  T extends PanoramaHttpApi.AnyApiDefinition,
  SelectResult = PanoramaHttpApi.ParsedResponse<T>
>(
  definition: T,
  ...args: QueryArgs<T, SelectResult>
) => {
  const [params, options = {}, body] = args
  const apiClient = useApiClient()
  return useQuery<PanoramaHttpApi.ParsedResponse<T>, PanoramaHttpApi.MappedError<T>, SelectResult>({
    ...panoramaQueryOptions(definition, { apiClient, params, body }),
    ...options
  })
}

/**
 * The key and fetcher for one call to a definition, as react-query options.
 *
 * Separate from the hook so that an imperative read — `ensureQueryData` from
 * code that cannot call hooks — lands on the same cache entry a component's
 * `usePanoramaQuery` would. A second key builder would not be wrong so much as
 * invisible: it would quietly fetch again and cache beside the first.
 */
export const panoramaQueryOptions = <T extends PanoramaHttpApi.AnyApiDefinition>(
  definition: T,
  {
    apiClient,
    params,
    body
  }: {
    apiClient: ApiClient
    params?: PanoramaHttpApi.PathParams<T>
    body?: PanoramaHttpApi.RawRequestBody<T>
  }
) => {
  const queryKey = definition.queryKey?.(params) ?? [definition.pathFor(params)]
  queryKey.push(apiClient)
  if (body !== undefined) queryKey.push(body)
  return {
    staleTime: definition.ttlSeconds,
    queryKey,
    queryFn: (): Promise<PanoramaHttpApi.ParsedResponse<T>> =>
      apiClient.callApi(
        definition,
        params as PanoramaHttpApi.PathParams<T>,
        body as PanoramaHttpApi.RawRequestBody<T>
      )
  }
}

type InfiniteQueryOptions<T extends PanoramaHttpApi.AnyApiDefinition> = Omit<
  UseInfiniteQueryOptions<
    PanoramaHttpApi.ParsedResponse<T>,
    PanoramaHttpApi.MappedError<T>,
    PanoramaHttpApi.ParsedResponse<T>['items']
  >,
  'queryFn' | 'getNextPageParam' | 'queryKey' | 'initialPageParam'
>

type InfiniteQueryArgs<T extends PanoramaHttpApi.AnyApiDefinition> =
  IsVoid<PanoramaHttpApi.PathParams<T>> extends true
    ? [params?: undefined, options?: InfiniteQueryOptions<T>]
    : [params: PanoramaHttpApi.PathParams<T>, options?: InfiniteQueryOptions<T>]

/**
 * A wrapper around `useInfiniteQuery` that automatically infers the types of the
 * query function and the error type from the API definition.
 *
 * Note that this requires an API that adheres to the pattern of returning
 * a `continueFrom` parameter in the response, which is used to fetch the next
 * page of results, also as a `continueFrom` parameter in the request.
 *
 * Ditto for requiring an `items` array in the response.
 *
 * You can supply other options to the query, such as `enabled`, `refetchOnWindowFocus`, etc.
 * but you cannot override the `queryKey` or `queryFn` options, as they are
 * automatically generated from the API definition.
 */
export const usePanoramaInfiniteQuery = <
  Response extends { items: unknown[]; continueFrom?: string; limit?: number },
  T extends PanoramaHttpApi.ResponseApiDefinition<z.ZodType<Response>>
>(
  definition: T,
  ...args: InfiniteQueryArgs<T>
) => {
  const [params, options = {}] = args
  const apiClient = useApiClient()
  const queryKey = definition.queryKey?.(params) ?? [definition.pathFor(params)]
  queryKey.push(apiClient)

  return useInfiniteQuery<
    PanoramaHttpApi.ParsedResponse<T>,
    PanoramaHttpApi.MappedError<T>,
    PanoramaHttpApi.ParsedResponse<T>['items']
  >({
    queryKey,
    queryFn: ({ pageParam }) =>
      apiClient.callApi(definition, {
        ...params,
        continueFrom: pageParam
      } as PanoramaHttpApi.PathParams<T>),
    getNextPageParam: (lastPage) => lastPage.continueFrom,
    initialPageParam: undefined as string | undefined,
    ...options,
    select: (data) => data.pages.flatMap((p) => p.items)
  })
}

type MutationVariables<T extends PanoramaHttpApi.AnyApiDefinition> =
  IsVoid<PanoramaHttpApi.PathParams<T>> extends true
    ? IsVoid<PanoramaHttpApi.RawRequestBody<T>> extends true
      ? void
      : { body: PanoramaHttpApi.RawRequestBody<T> }
    : IsVoid<PanoramaHttpApi.RawRequestBody<T>> extends true
      ? { params: Omit<PanoramaHttpApi.PathParams<T>, 'l'> }
      : {
          params: Omit<PanoramaHttpApi.PathParams<T>, 'l'>
          body: PanoramaHttpApi.RawRequestBody<T>
        }

type MutationOptions<T extends PanoramaHttpApi.AnyApiDefinition> = Omit<
  UseMutationOptions<
    PanoramaHttpApi.ParsedResponse<T>,
    PanoramaHttpApi.MappedError<T>,
    MutationVariables<T>
  >,
  'mutationFn'
>

/**
 * The query keys a mutation declares it invalidates. Mutations already apply
 * these on success, so this is for replaying them when the write they describe
 * lands after the response.
 */
export const invalidationKeysFor = <T extends PanoramaHttpApi.AnyApiDefinition>(
  definition: T,
  params: PanoramaHttpApi.PathParams<T> | undefined
): unknown[][] => {
  const invalidation = definition.invalidateQueries?.(params)

  if (invalidation === undefined || invalidation.length === 0) return []

  // A single key's first element is a string/object (orgCode, a tag); a
  // multi-key result's first element is itself a key array. Normalize to a
  // list of keys so contracts may return either form.
  return Array.isArray(invalidation[0])
    ? (invalidation as unknown[][])
    : [invalidation as unknown[]]
}

/**
 * A wrapper around `useMutation` that automatically infers the types of the
 * mutation function and the error type from the API definition.
 *
 * You can supply other options to the mutation, such as `onSuccess`, `onError`, etc.
 * but you cannot override the `mutationFn` option, as it is automatically
 * generated from the API definition.
 */
export const usePanoramaMutation = <T extends PanoramaHttpApi.AnyApiDefinition>(
  definition: T,
  options: MutationOptions<T> = {}
) => {
  const apiClient = useApiClient()
  const queryClient = useQueryClient()

  return useMutation<
    PanoramaHttpApi.ParsedResponse<T>,
    PanoramaHttpApi.MappedError<T>,
    MutationVariables<T>
  >({
    ...options,
    onError: (error, variables, onMutateResult, context) => {
      options.onError?.(error, variables, onMutateResult, context)
      console.error({
        error,
        variables,
        message: 'Error in Panorama mutation'
      })
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      const typedVariables = variables as {
        params?: PanoramaHttpApi.PathParams<T>
        body?: PanoramaHttpApi.RawRequestBody<T>
      }
      invalidationKeysFor(definition, typedVariables?.params).forEach((queryKey) =>
        queryClient.invalidateQueries({ queryKey })
      )
      options.onSuccess?.(data, variables, onMutateResult, context)
    },
    mutationFn: (variables) => {
      const typedVariables = variables as {
        params?: PanoramaHttpApi.PathParams<T>
        body?: PanoramaHttpApi.RawRequestBody<T>
      }
      return apiClient.callApi(
        definition,
        typedVariables?.params as PanoramaHttpApi.PathParams<T>,
        typedVariables?.body as PanoramaHttpApi.RawRequestBody<T>
      )
    }
  })
}
